import { waitForImages } from "@utils/imageLoadUtils";
import { breakpointConfig } from "@utils/breakpointConfigUtils";
import Swiper from "swiper";
import { Pagination, A11y, Navigation } from 'swiper/modules';
import { justifiedGallery, JustifiedGalleryController } from "./justifiedGallery";

class ImageGallery {
  private el: HTMLElement;
  private carouselAmountMedium: number;
  private carouselAmountLarge: number;
  private fixedColSizeClasses: string | null;
  private isCarousel: boolean;
  private isCarouselFlexible: boolean;
  private isJustifiedMasonry: boolean;
  private initialCount: number;
  private imageGalleryList: HTMLElement;
  private loadMoreLink: HTMLElement;
  private remainingAmount: HTMLElement;
  private parentAccordionDetails: HTMLElement | null;
  private isInAccordion: boolean;
  private parentTab: HTMLElement;
  private isInTab: boolean;
  private galleryWrapper: HTMLElement;
  private galleryItemClasses: string;
  private galleryItemsCounter: number;
  private initialGalleryCounter: number;
  private imageList: any[];
  private imageGalleryJustified: HTMLElement | null;
  private justifiedGalleryController: JustifiedGalleryController | null;
  private template: HTMLTemplateElement;
  private swiper: Swiper;
  private pagination: HTMLElement;
  private swiperPrevButton: HTMLElement;
  private swiperNextButton: HTMLElement;


  constructor(el: HTMLElement) {
    this.el = el;

    this.carouselAmountMedium = parseInt(this.el.getAttribute("data-amount-md"));
    this.carouselAmountLarge = parseInt(this.el.getAttribute("data-amount-lg"));

    this.fixedColSizeClasses = this.el.getAttribute("data-col-sizes");

    this.isCarousel = this.el.getAttribute("data-is-carousel") === "true";
    this.isCarouselFlexible = this.el.getAttribute("data-is-flexible-carousel") === "true";
    this.isJustifiedMasonry = this.el.getAttribute("data-js-image-gallery-justified") === "true";

    this.initialCount = parseInt(this.el.getAttribute("data-initial-count"));

    this.imageGalleryList = this.el.querySelector(".js-image-list");
    this.loadMoreLink = this.el.querySelector(".js-load-more-images-link");
    this.remainingAmount = this.el.querySelector(".js-remaining-amount");
    this.parentAccordionDetails = this.el.closest(".comp-accordion details");
    this.isInAccordion = this.parentAccordionDetails !== null;
    this.parentTab = this.el.closest(".comp-tab");
    this.isInTab = this.parentTab !== null;

    this.galleryWrapper = document.createElement("div");

    this.galleryItemClasses = "";

    this.template = this.el.querySelector(".js-image-gallery-template");
  }

  public init = () => {
    this.initImageList();

    if (this.isCarousel) {
      this.galleryItemsCounter = 0;
      this.initialGalleryCounter = 0;

      if (this.isInAccordion) {
        this.parentAccordionDetails?.addEventListener("toggle", this.initCarouselGallery.bind(this));
      } else if (this.isInTab) {
        document.addEventListener("tabsChanged", this.initCarouselGallery.bind(this));
      }
      this.initCarouselGallery();
    } else if (this.isJustifiedMasonry) {
      this.el.classList.add("comp-image-gallery-justified");

      if (this.isInAccordion) {
        this.parentAccordionDetails?.addEventListener("toggle", this.initJustifiedGallery.bind(this));
      } else if (this.isInTab) {
        document.addEventListener("tabsChanged", this.initJustifiedGallery.bind(this));
      }
      this.initJustifiedGallery();
    } else {
      this.initDefaultGallery();
    }
  }

  private initImageList = () => {
    this.imageList = [];

    this.imageGalleryList.querySelectorAll("li").forEach((li) => {
      const image = {
        imageSourceXs: li.getAttribute("data-image-src-xs"),
        imageSourceS: li.getAttribute("data-image-src-s"),
        imageSourceM: li.getAttribute("data-image-src-m"),
        imageSourceL: li.getAttribute("data-image-src-l"),
        imageSourceXl: li.getAttribute("data-image-src-xl"),
        imageSourceFsXs: li.getAttribute("data-image-src-fs-xs"),
        imageSourceFsS: li.getAttribute("data-image-src-fs-s"),
        imageSourceFsM: li.getAttribute("data-image-src-fs-m"),
        imageSourceFsL: li.getAttribute("data-image-src-fs-l"),
        imageSrc: li.getAttribute("data-image-src"),
        imageGalleryId: ":" + li.getAttribute("data-image-gallery-id"),
        imageColSize: this.galleryItemClasses,
        imageAltText: li.getAttribute("data-image-alt"),
        imageLoading: li.getAttribute("data-image-loading"),
        imageHeight: li.getAttribute("data-image-height"),
        imageWidth: li.getAttribute("data-image-width"),
        imageLink: li.getAttribute("data-image-link"),
        imageLinkTarget: li.getAttribute("data-image-link-target"),
        imageCaption: li.getAttribute("data-image-caption")
          ? li.getAttribute("data-image-caption")
          : false,
      };

      this.imageList.push(image);
    })
  }

  private createPaginationAndNavigation = () => {
    this.pagination = document.createElement("div");
    this.swiperPrevButton = document.createElement("div");
    this.swiperNextButton = document.createElement("div");

    this.pagination.className = "swiper-pagination";
    this.swiperPrevButton.className = "swiper-prev swiper-arrow";
    this.swiperNextButton.className = "swiper-next swiper-arrow";

    this.el.appendChild(this.swiperPrevButton);
    this.el.appendChild(this.swiperNextButton);
    this.el.appendChild(this.pagination);
  }

  private initSwiper = () => {
    this.createPaginationAndNavigation();

    const swiperContainer = this.el.querySelector<HTMLElement>(".swiper");

    this.swiper = new Swiper(swiperContainer, {
      init: true,
      loop: true,
      allowTouchMove: false,
      roundLengths: true,
      enabled: true,
      modules: [Pagination, A11y, Navigation],
      speed: 350,
      pagination: {
        el: this.pagination,
        type: "bullets",
        clickable: true,
        bulletClass: "swiper-bullet",
        bulletActiveClass: "swiper-bullet-active",
      },
      slidesPerView: 1.25,
      spaceBetween: this.isCarouselFlexible ? 4 : 24,
      centeredSlides: true,
      slidesPerGroup: 1,
      navigation: {
        nextEl: this.swiperNextButton,
        prevEl: this.swiperPrevButton,
        disabledClass: "swiper-disabled"
      },
      breakpoints: {
        // Tablet
        768: {
          slidesPerView: this.isCarouselFlexible ? "auto" : this.carouselAmountMedium,
          slidesPerGroup: this.isCarouselFlexible
            ? this.carouselAmountMedium - 1
            : this.carouselAmountMedium,
          centeredSlides: false,
        },
        // Desktop and above
        1024: {
          slidesPerView: this.isCarouselFlexible ? "auto" : this.carouselAmountLarge,
          slidesPerGroup: this.isCarouselFlexible
            ? this.carouselAmountLarge - 1
            : this.carouselAmountLarge,
          centeredSlides: false,
        }
      }
    });
  }

  private initCarouselGallery = () => {
    if (this.isParentAccordionPanelInactive() || this.isParentTabInactive()) {
      return;
    }

    // Guard against duplicate initialization when tabsChanged / toggle fire multiple times.
    if (this.el.querySelector(":scope > .swiper")) {
      return;
    }

    const handleNumberOfGalleryItems = (html: DocumentFragment) => {
      if (
        this.initialGalleryCounter === 0 &&
        this.galleryItemsCounter !== 0
      ) {
        this.initialGalleryCounter = this.galleryItemsCounter;
      }

      if (
        this.galleryItemsCounter === 0 ||
        this.galleryItemsCounter <= this.initialGalleryCounter
      ) {
        this.galleryWrapper.append(html);
      }
    };

    if (!this.isCarouselFlexible) {
      this.galleryWrapper.classList.add("js-image-gallery-carousel", "swiper-wrapper");
      this.galleryItemClasses = `${this.fixedColSizeClasses} swiper-slide`;

      this.imageList.forEach((value) => {
        const html = this.renderImage(
          { ...value, imageColSize: this.galleryItemClasses },
        );
        handleNumberOfGalleryItems(html);
      });
    } else {
      this.galleryWrapper.classList.add("js-image-gallery-carousel", "is-justified-carousel", "swiper-wrapper");

      this.imageList.forEach((value) => {
        const html = this.renderImage({ ...value });

        handleNumberOfGalleryItems(html);
      });

      Array.from(this.galleryWrapper.children).forEach(child => child.classList.add("swiper-slide"));
    }

    const swiperContainer = document.createElement("div");
    swiperContainer.className = "swiper";
    swiperContainer.append(this.galleryWrapper);

    this.el.append(swiperContainer);

    this.initSwiper();
  }

  private initJustifiedGallery = () => {
    if (
      this.galleryWrapper.classList.contains("js-image-gallery-justified") ||
      this.isParentAccordionPanelInactive() ||
      this.isParentTabInactive()
    ) {
      return;
    }

    this.galleryWrapper.classList.add("js-image-gallery-justified", "justified-gallery");

    this.imageList.forEach((value) => {
      const html = this.renderImage({ ...value });
      this.galleryWrapper.append(html);
    });

    this.galleryWrapper.querySelectorAll<HTMLElement>("div:has(.comp-image)").forEach(el => el.style.display = "none");
    Array.from(this.galleryWrapper.querySelectorAll<HTMLElement>("div:has(.comp-image)"))
      .slice(0, this.initialCount)
      .forEach(el => el.style.display = "");

    this.el.append(this.galleryWrapper);

    waitForImages(this.galleryWrapper)
      .then(() => this.initJustified());

    this.initLoadMore("div:has(.comp-image)");
  }

  private initLoadMore = (selector: string) => {
    if (this.imageList.length > this.initialCount) {
      this.loadMoreLink.classList.remove("hidden");
      this.el.appendChild(this.loadMoreLink);
    }

    const numberOfHiddenImages = this.galleryWrapper.querySelectorAll(selector + '[style="display: none;"]').length;

    this.remainingAmount.innerText = numberOfHiddenImages?.toString() || "";

    this.loadMoreLink.addEventListener("click", async (e) => {
      e.preventDefault();

      const newlyRevealed: HTMLElement[] = [];

      Array.from(this.galleryWrapper.querySelectorAll<HTMLElement>(selector))
        .filter(el => el.style.display === "none")
        .slice(0, this.initialCount)
        .forEach(el => {
          el.style.display = "";
          if (this.isJustifiedMasonry) el.style.visibility = "hidden";
          newlyRevealed.push(el);
        });

      if (this.isJustifiedMasonry && this.imageGalleryJustified) {
        await Promise.all(newlyRevealed.map(el => waitForImages(el)));
        this.justifiedGalleryController?.update();
        newlyRevealed.forEach(el => el.style.visibility = "");
      }

      const hiddenImagesLength = Array.from(this.galleryWrapper.querySelectorAll<HTMLElement>(selector))
        .filter(el => el.style.display === "none").length;

      this.remainingAmount.innerText = hiddenImagesLength?.toString() || "";

      if (Array.from(this.galleryWrapper.querySelectorAll<HTMLElement>(selector)).filter(el => el.style.display === "none").length === 0) {
        this.loadMoreLink.classList.add("hidden");
      }
    });
  }

  private isParentTabInactive = () => {
    return this.isInTab && !this.parentTab.classList.contains("active-tab");
  }

  private initJustified = () => {
    this.imageGalleryJustified = this.el.querySelector(
      ".js-image-gallery-justified",
    );

    if (this.imageGalleryJustified && this.imageGalleryJustified.offsetHeight <= 160) {
      this.justifiedGalleryController = justifiedGallery(this.imageGalleryJustified, {
        margins: 2,
        rowHeight: 165,
        selector: 'div:has(.comp-image)',
        lastRow: 'nojustify',
      });
    }
  }

  private initDefaultGallery = () => {
    this.galleryWrapper.classList.add("row");
    this.galleryItemClasses = this.fixedColSizeClasses;

    this.imageList.forEach((value) => {
      const html = this.renderImage(
        { ...value, imageColSize: this.galleryItemClasses },
      );
      this.galleryWrapper.append(html);
    });

    const galleryItems = Array.from(this.galleryWrapper.querySelectorAll<HTMLElement>('div[class*="col"]'));

    galleryItems.forEach(el => el.style.display = "none");
    galleryItems.slice(0, this.initialCount).forEach(el => el.style.display = "");

    this.el.append(this.galleryWrapper);
    this.initLoadMore('div[class*="col"]');
  }

  private isParentAccordionPanelInactive = () => {
    const isPanelClosed = !this.parentAccordionDetails?.hasAttribute("open");

    return this.isInAccordion && isPanelClosed;
  }

  private renderImage = (imageData): DocumentFragment => {
    const clone = this.template.content.cloneNode(true) as DocumentFragment;

    // Column wrapper
    const colDiv = clone.querySelector("[data-col-size]");
    if (imageData.imageColSize) {
      colDiv?.classList.add(...imageData.imageColSize.trim().split(/\s+/));
      colDiv?.removeAttribute("data-col-size");
    } else {
      // No column wrapper needed - unwrap the content
      colDiv?.removeAttribute("data-col-size");
    }

    // Fullview lightbox link
    const fullviewLink = clone.querySelector("[data-href]");
    if (fullviewLink) {
      const lcHref = [
        `${imageData.imageSourceFsXs} ${breakpointConfig.xsmall_min}w`,
        `${imageData.imageSourceFsS} ${breakpointConfig.small_min}w`,
        `${imageData.imageSourceFsM} ${breakpointConfig.medium_min}w`,
        `${imageData.imageSourceFsL} ${breakpointConfig.large_min}w`,
      ].join(" | ");
      fullviewLink.setAttribute("href", imageData.imageSourceFsL);
      fullviewLink.setAttribute("data-lc-href", lcHref);
      fullviewLink.setAttribute("data-rel", "lightbox" + imageData.imageGalleryId);
      fullviewLink.removeAttribute("data-href");
      if (imageData.imageCaption) {
        fullviewLink.setAttribute("title", imageData.imageCaption);
      }
      fullviewLink.removeAttribute("data-title");
    }

    // Regular image link
    const imageLink = clone.querySelector("[data-link-href]");
    if (imageLink) {
      if (imageData.imageLink) {
        imageLink.setAttribute("href", imageData.imageLink);

        if (imageData.imageLinkTarget) {
          imageLink.setAttribute("target", imageData.imageLinkTarget);
        } else {
          imageLink.removeAttribute("target");
        }

        imageLink.removeAttribute("data-link-href");
        imageLink.removeAttribute("data-link-target");
        clone.querySelector(".js-link-icon")?.classList.remove("hidden");
      } else {
        // No link - remove the anchor wrapper
        imageLink.replaceWith(...Array.from(imageLink.childNodes));
      }
    }

    // Picture sources
    const srcXs = clone.querySelector("[data-srcset-xs]");
    srcXs?.setAttribute("srcset", imageData.imageSourceXs);
    srcXs?.removeAttribute("data-srcset-xs");

    const srcS = clone.querySelector("[data-srcset-s]");
    srcS?.setAttribute("srcset", imageData.imageSourceS);
    srcS?.removeAttribute("data-srcset-s");

    const srcM = clone.querySelector("[data-srcset-m]");
    srcM?.setAttribute("srcset", imageData.imageSourceM);
    srcM?.removeAttribute("data-srcset-m");

    const srcL = clone.querySelector("[data-srcset-l]");
    srcL?.setAttribute("srcset", imageData.imageSourceL);
    srcL?.removeAttribute("data-srcset-l");

    const srcXl = clone.querySelector("[data-srcset-xl]");
    srcXl?.setAttribute("srcset", imageData.imageSourceXl);
    srcXl?.removeAttribute("data-srcset-xl");

    const srcXxl = clone.querySelector("[data-srcset-xxl]");
    srcXxl?.setAttribute("srcset", imageData.imageSourceXl);
    srcXxl?.removeAttribute("data-srcset-xxl");

    const srcXxxl = clone.querySelector("[data-srcset-xxxl]");
    srcXxxl?.setAttribute("srcset", imageData.imageSourceXl);
    srcXxxl?.removeAttribute("data-srcset-xxxl");

    // Image element
    const img = clone.querySelector("[data-src]");
    img?.setAttribute("src", imageData.imageSourceXl);
    img?.setAttribute("alt", imageData.imageAltText || "");
    img?.setAttribute("loading", this.isCarousel ? "eager" : imageData.imageLoading || "lazy");
    img?.setAttribute("height", imageData.imageHeight || "");
    img?.setAttribute("width", imageData.imageWidth || "");
    img?.removeAttribute("data-src");
    img?.removeAttribute("data-alt");
    img?.removeAttribute("data-loading");
    img?.removeAttribute("data-height");
    img?.removeAttribute("data-width");

    // Caption
    const caption = clone.querySelector("[data-caption]");
    if (imageData.imageCaption) {
      if (caption) {
        caption.textContent = imageData.imageCaption;
        caption.classList.remove("hidden");
        caption.removeAttribute("data-caption");
      }
    } else {
      caption?.remove();
    }

    return clone;
  }
}

const components = {
  "image-gallery": ImageGallery
};

export function initComponents(root = document) {
  Object.entries(components).forEach(([name, Component]) => {
    root.querySelectorAll<HTMLElement>(`[data-component="${name}"]`).forEach(el => {
      new Component(el).init();
    });
  });
}

if (document.readyState !== "loading") {
  initComponents();
} else {
  document.addEventListener("DOMContentLoaded", () => initComponents());
}
